import type { SearchStudent } from '@appTypes/shell';
import { ROUTES } from '@constants/routes';
import { SearchOutlined } from '@mui/icons-material';
import {
  Box,
  OutlinedInput,
  InputAdornment,
  Typography,
  Paper,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import { searchService } from '@services/search.service';
import { useAppShellStore } from '@store/appShellStore';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const GlobalSearchBar = () => {
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const centreId = useAppShellStore((s) => s.centreId);

  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      setOpen(false);
      setLoading(false); // clearing the field must stop the spinner
      return;
    }

    const ctrl = new AbortController();

    // Only show the spinner once a request actually fires (after the debounce),
    // so a cleared/aborted cycle can never leave it stuck spinning.
    const t = setTimeout(() => {
      setLoading(true);
      searchService
        .search(term, ctrl.signal)
        .then(({ data }) => {
          setResults(data.students);
          setOpen(true);
        })
        .catch((err: { name?: string }) => {
          if (err?.name !== 'CanceledError') {
            setResults([]);
            setOpen(true);
          }
        })
        .finally(() => setLoading(false));
    }, 250);

    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const handleSelect = (student: SearchStudent) => {
    setQ('');
    setOpen(false);

    // The search response carries the admission id, so there's no follow-up
    // request to fail. It used to fall back to the admissions list, which
    // refuses a rollup scope — the rejection was swallowed and the click
    // silently did nothing.
    if (!student.admissionId) return;
    const path = ROUTES.STUDENT_DETAIL.replace(':admissionId', student.admissionId);

    // A hit found through the "All centres" rollup belongs to one specific
    // centre, and the detail page is centre-scoped. Switch to that centre first
    // so the page can actually load, rather than landing on the "pick a centre"
    // gate. Hard navigation, matching how every other centre change re-bootstraps
    // the app on the new scope.
    if (student.centreId && student.centreId !== centreId) {
      useAppShellStore.getState().setCentreId(student.centreId);
      window.location.assign(path);
      return;
    }

    // Search can be used FROM the detail page, so a plain history-back would
    // return to the previously-viewed student. Declare Admissions as the origin
    // so Back always leaves the student chain rather than walking it.
    navigate(path, { state: { backTo: ROUTES.ADMISSIONS } });
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={wrapperRef} sx={{ position: 'relative' }}>
        <OutlinedInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Search Student by Admission No., Name, or Roll No."
          startAdornment={
            <InputAdornment position="start">
              {loading
                ? <CircularProgress size={15} sx={(theme) => ({ color: theme.palette.grey[400] })} />
                : <SearchOutlined sx={(theme) => ({ fontSize: 17, color: theme.palette.grey[400] })} />
              }
            </InputAdornment>
          }
          fullWidth
          sx={(theme) => ({
            height: 47,
            borderRadius: '20px',
            bgcolor: theme.palette.grey[50],
            fontSize: 14,
            color: theme.palette.slate[900],
            '& input::placeholder': { color: theme.palette.grey[400], opacity: 1 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[200] },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400] },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, borderWidth: 1 },
          })}
        />

        {open && (
          <Paper
            elevation={0}
            sx={(theme) => ({
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              mt: '6px',
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              overflow: 'hidden',
            })}
          >
            {results.length === 0 ? (
              <Box sx={{ px: '16px', py: '14px' }}>
                <Typography sx={(theme) => ({ fontSize: '13px', color: theme.palette.slate[400] })}>No results found</Typography>
              </Box>
            ) : (
              results.map((r) => (
                <Box
                  key={r._id}
                  onClick={() => handleSelect(r)}
                  sx={(theme) => ({
                    px: '16px',
                    py: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    '&:hover': { bgcolor: theme.palette.grey[50] },
                    borderBottom: `1px solid ${theme.palette.slate[100]}`,
                    '&:last-child': { borderBottom: 'none' },
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      width: 32, height: 32, bgcolor: theme.palette.status.info.bg, borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    })}
                  >
                    <Typography sx={(theme) => ({ fontSize: '11px', fontWeight: 700, color: theme.palette.primary.main })}>
                      {r.rollNo.slice(-2)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={(theme) => ({ fontSize: '13px', fontWeight: 600, color: theme.palette.grey[900], lineHeight: '18px' })}>
                      {r.name}
                    </Typography>
                    <Typography sx={(theme) => ({ fontSize: '11px', color: theme.palette.slate[500], lineHeight: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
                      {r.rollNo} · {r.courseId.name} · s/o {r.fatherName}
                    </Typography>
                  </Box>

                  {/* Only cancelled is called out — confirmed is the norm and a
                      badge on every row would just be noise. Uses the shared
                      status tokens rather than repeating the palette, and
                      never shrinks, so the name truncates instead of it. */}
                  {r.admissionStatus === 'cancelled' && (
                    <Box
                      sx={(theme) => ({
                        display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0,
                        px: '10px', py: '4px', borderRadius: '9999px',
                        bgcolor: theme.palette.status.error.bg,
                        border: `1px solid ${theme.palette.status.error.dot}`,
                      })}
                    >
                      <Box sx={(theme) => ({ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.status.error.dot, flexShrink: 0 })} />
                      <Typography sx={(theme) => ({ fontSize: 11, fontWeight: 600, color: theme.palette.status.error.dot, whiteSpace: 'nowrap' })}>
                        Cancelled
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
